/**
 * ListPage — andamiaje ÚNICO de los listados del CMDB.
 *
 * POR QUÉ EXISTE
 * ---------------------------------------------------------------------------
 * Las 29 listas del CMDB se habían hecho por copia: cada una llevaba su propia
 * barra de búsqueda, su export, su menú de columnas y sus botones de fila. El
 * CSS ya se unificó en `styles/lists.css` (las clases `list-*`), pero el JSX
 * seguía duplicado, así que cada lista derivaba a su aire: unas con copiar,
 * otras sin; unas con el menú de columnas que no se cierra al pulsar fuera,
 * otras sin filtros...
 *
 * Este componente es el único sitio donde vive ese andamiaje. Una lista nueva
 * solo aporta: título, columnas, filas, filtros y las acciones propias de su
 * elemento. Lo demás (buscador, exportar a Excel, columnas mostrar/ocultar,
 * cebra de filas, botones Copiar/Editar/Eliminar, diálogo de borrado, estados
 * de carga/vacío, clic en fila para abrir la ficha) es común y ya está hecho.
 *
 * CONTRATO (lo que ve el usuario, idéntico en todas las listas)
 * ---------------------------------------------------------------------------
 *  1. Buscar → filtros propios del elemento → exportar a Excel → columnas.
 *  2. Las columnas visibles se recuerdan por lista (`columnStorageKey`).
 *  3. Clic en cualquier parte de la fila abre la ficha EN MODO LECTURA.
 *     Los botones de la columna de acciones cortan la propagación.
 *  4. Botones por fila, siempre en este orden:
 *        [acciones propias del elemento] · Copiar · Editar · Eliminar
 *  5. Filas con color alternado (lo pinta `lists.css` con `:nth-child`).
 *  6. Si hay diálogo de borrado: Esc cierra, click en el fondo cierra.
 *
 * ESTÁNDAR PARA LISTAS NUEVAS
 * ---------------------------------------------------------------------------
 * Usa `list-*` para las clases y NO añadas CSS propio: si falta algo, va a
 * `styles/lists.css`. Documentado en `docs/ESTANDAR_LISTADOS.md`.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
  FunnelIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import ExportExcelButton from '../ExportExcelButton';
import Dialog from './Dialog';
import ImportDialog from './ImportDialog';
import LocationFilter from '../LocationFilter';
import ViewSwitcher from './ViewSwitcher';
import { useListView } from '../../views/ViewsContext';
import useCompactViewport from '../../hooks/useCompactViewport';
import api from '../../services/api';

/**
 * ¿El filtro tiene un valor puesto? (para el contador del botón «Filtros»).
 * Cubre los tres tipos que admite la barra: desplegable (texto), ubicaciones
 * (array) e interruptor (booleano).
 */
function isFilterFilled(filter) {
  const value = filter?.value;
  if (filter?.type === 'toggle') return Boolean(value);
  if (Array.isArray(value)) return value.length > 0;
  return value !== '' && value !== null && value !== undefined;
}

/** Compara dos valores de celda para el orden por columna. */
function compareValues(a, b) {
  const emptyA = a === null || a === undefined || a === '';
  const emptyB = b === null || b === undefined || b === '';
  if (emptyA && emptyB) return 0;
  if (emptyA) return 1;          // los vacíos siempre al final
  if (emptyB) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  const sa = String(a);
  const sb = String(b);
  return sa.localeCompare(sb, 'es', { numeric: true, sensitivity: 'base' });
}

export default function ListPage({
  /* ── Cabecera ── */
  icon: Icon,
  title,
  subtitle,
  onCreate,
  createLabel,
  createTitle,
  createDisabled = false,

  /* ── Barra de herramientas ── */
  search,
  onSearchChange,
  searchPlaceholder = 'Buscar…',
  filters = [],
  exportRows,
  exportFileBase = 'listado',
  exportSheetName,

  /* ── Columnas y filas ── */
  columns = [],
  columnStorageKey,
  defaultVisible,
  /** Empresa activa: necesaria para guardar el orden de columnas en el servidor. */
  companyId,
  /**
   * Clave de entidad del importador (`servers`, `switches`, `racks`…).
   * Si se pasa, la cabecera muestra «Importar» y el diálogo genérico.
   */
  importEntity,
  importTitle,
  /** Avisa al listado de que se importaron filas (para recargar). */
  onImported,
  rows = [],
  rowKey = (row) => row.id,
  rowId,
  onRowClick,
  rowClassName,
  rowTitle,
  /** Agrupación opcional de la tabla: `(row) => 'etiqueta del grupo'` (null = sin grupo). */
  groupBy,
  /**
   * Selección múltiple para ediciones masivas. `true` (o una función
   * `(row) => bool` que decida fila a fila, p. ej. solo las que el usuario
   * puede mover). Con `bulkActions` aparece una barra sobre la tabla mientras
   * haya algo seleccionado.
   */
  selectable = false,
  /** `({ selectedRows, clearSelection }) => ReactNode` — botones de la barra. */
  bulkActions,
  /** Cambia este número para vaciar la selección (p. ej. tras aplicar el cambio). */
  selectionResetKey = 0,

  /* ── Vistas (lista / fichas / árbol) ── */
  views,
  listKey,
  defaultView = 'list',
  /**
   * Avisa de la vista activa (también al montar y al restaurar la guardada).
   * Existe para el caso de DOS conjuntos de datos en la misma pantalla: el
   * listado de Nomenclatura enseña «Reglas» o «Contadores» según la vista, y
   * necesita saber cuál está puesta para cambiar filas, filtros y KPIs.
   */
  onViewChange,

  /* ── Acciones por fila ── */
  actions = {},

  /* ── Estados ── */
  loading = false,
  loadingText = 'Cargando…',
  error,
  empty = {},

  /* ── Extras ── */
  onSortChange,
  kpis,
  headerActions,   // botones propios de la pantalla, en la cabecera (junto a vistas y «crear»)
  /**
   * Listado DENTRO de una sección con pestañas (p. ej. Agente CMDB).
   * La página ya tiene su cabecera (`<h1>` + pestañas), así que este listado se
   * pinta como sección: título menor (`<h2>`), icono pequeño y sin aire extra.
   * No usar en pantallas que son una lista por sí solas.
   */
  embedded = false,
  className = '',
}) {
  const { t } = useTranslation();
  const tf = (key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  };

  /* ── Vista activa (por defecto la que declara el componente) ─────────── */
  const viewDefs = Array.isArray(views) ? views : [];
  const {
    view: activeView,
    setView,
    reset: resetView,
    isDefault: isDefaultView,
  } = useListView(listKey, defaultView);
  const activeViewDef = viewDefs.find((v) => v.key === activeView);
  // Una vista sin `render` propio se pinta con la TABLA estándar (es el caso de
  // «lista» y «árbol», que solo cambian el orden/indentación de las filas). Si
  // la preferencia guardada apunta a una vista que este listado ya no
  // implementa, se pinta la tabla en vez de dejar la pantalla vacía.
  const showsTable = !activeViewDef || !activeViewDef.render;
  // Una vista propia puede pedir pintarse aunque no haya filas (la tabla editable
  // de tomas tiene que enseñar su botón «Añadir fila» con la lista vacía).
  const viewRendersEmpty = Boolean(
    !showsTable && activeViewDef?.render && activeViewDef?.renderWhenEmpty,
  );

  /* ── Columnas visibles (persistidas) ─────────────────────────────────── */
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const all = columns.map((c) => c.key);
    // Un listado con muchas columnas puede declarar cuáles se ven de entrada
    // (servidores: 22 columnas, 13 visibles). Lo que el usuario elija manda.
    const fallback = Array.isArray(defaultVisible)
      ? defaultVisible.filter((k) => all.includes(k))
      : all;
    if (!columnStorageKey) return fallback;
    try {
      const saved = JSON.parse(localStorage.getItem(columnStorageKey) || 'null');
      // Se conservan las guardadas que aún existen (por si la lista cambia).
      if (Array.isArray(saved)) return saved.filter((k) => all.includes(k));
    } catch { /* localStorage ilegible: se usa el valor por defecto */ }
    return fallback;
  });
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const columnMenuRef = useRef(null);        // el botón (rueda dentada de la cabecera)
  const columnPopupRef = useRef(null);       // el desplegable, que vive fuera de la tabla
  const [columnMenuPos, setColumnMenuPos] = useState(null);

  /* ── Orden de columnas (persistente por usuario) ─────────────────────────
     `visibleColumns` dice CUÁLES se ven; `columnOrder` en qué orden. El orden
     incluye también las ocultas para que al volver a mostrarlas recuperen su
     sitio. Se guarda en el servidor (por usuario + empresa) y en
     `localStorage` como caché para que la primera pintura no parpadee. */
  const listPrefsKey = listKey
    || (columnStorageKey ? columnStorageKey.replace(/^cmdb_cols_/, '') : null);

  const [columnOrder, setColumnOrder] = useState(() => {
    const all = columns.map((c) => c.key);
    if (!columnStorageKey) return all;
    try {
      const saved = JSON.parse(localStorage.getItem(`${columnStorageKey}__order`) || 'null');
      if (Array.isArray(saved)) {
        const kept = saved.filter((k) => all.includes(k));
        return [...kept, ...all.filter((k) => !kept.includes(k))];
      }
    } catch { /* localStorage ilegible */ }
    return all;
  });

  // El orden y la visibilidad se piden UNA vez por listado y empresa.
  const prefsLoadedRef = useRef(false);
  useEffect(() => {
    if (prefsLoadedRef.current || !companyId || !listPrefsKey) return;
    prefsLoadedRef.current = true;
    api.getColumnPreferences(companyId)
      .then((data) => {
        const saved = data?.columns?.[listPrefsKey];
        if (!saved) return;
        const all = columns.map((c) => c.key);
        const order = (saved.order || []).filter((k) => all.includes(k));
        if (order.length) {
          setColumnOrder([...order, ...all.filter((k) => !order.includes(k))]);
        }
        const visible = (saved.visible || []).filter((k) => all.includes(k));
        if (visible.length) setVisibleColumns(visible);
      })
      .catch(() => { /* sin preferencias guardadas: se queda lo de serie */ });
  }, [companyId, listPrefsKey, columns]);

  const persistColumns = (order, visible) => {
    if (columnStorageKey) {
      try {
        localStorage.setItem(`${columnStorageKey}__order`, JSON.stringify(order));
        localStorage.setItem(columnStorageKey, JSON.stringify(visible));
      } catch { /* cuota llena */ }
    }
    if (companyId && listPrefsKey) {
      api.saveColumnPreference(companyId, listPrefsKey, order, visible).catch(() => {});
    }
  };

  const moveColumn = (fromKey, toKey) => {
    if (!fromKey || !toKey || fromKey === toKey) return;
    const next = columnOrder.filter((k) => k !== fromKey);
    const target = next.indexOf(toKey);
    if (target === -1) return;
    next.splice(target, 0, fromKey);
    setColumnOrder(next);
    persistColumns(next, visibleColumns);
  };

  const resetColumns = () => {
    const all = columns.map((c) => c.key);
    const visible = Array.isArray(defaultVisible)
      ? defaultVisible.filter((k) => all.includes(k))
      : all;
    setColumnOrder(all);
    setVisibleColumns(visible);
    persistColumns(all, visible);
  };

  const [dragColumn, setDragColumn] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  // Arrastre de columnas desde la CABECERA de la tabla (además del menú).
  const [dragHeader, setDragHeader] = useState(null);
  const [headerDropTarget, setHeaderDropTarget] = useState(null);

  /** El desplegable de columnas se pinta con `position: fixed` porque la tabla
      hace scroll y un menú absoluto dentro del `thead` quedaría recortado. */
  const toggleColumnMenu = () => {
    if (!showColumnMenu) {
      const rect = columnMenuRef.current?.getBoundingClientRect();
      setColumnMenuPos(rect
        ? { top: rect.bottom + 6, right: Math.max(8, window.innerWidth - rect.right) }
        : null);
    }
    setShowColumnMenu((v) => !v);
  };

  const toggleColumn = (key) => {
    const next = visibleColumns.includes(key)
      ? visibleColumns.filter((k) => k !== key)
      : [...visibleColumns, key];
    setVisibleColumns(next);
    persistColumns(columnOrder, next);
  };

  /* Si cambia el JUEGO de columnas (o la preferencia guardada), las visibles se
     recalculan: se leen las de esa clave y se descartan las que ya no existen.
     Es lo que permite que dos vistas del mismo listado tengan columnas
     distintas (Reglas y Contadores en Nomenclatura) sin arrastrar las de la
     otra. El efecto no toca nada cuando el resultado es el mismo, así que no
     pelea con el usuario ni provoca bucles aunque el padre pase las columnas
     sin memoizar. */
  useEffect(() => {
    const all = columns.map((c) => c.key);
    const fallback = Array.isArray(defaultVisible)
      ? defaultVisible.filter((k) => all.includes(k))
      : all;
    let next = fallback;
    if (columnStorageKey) {
      try {
        const saved = JSON.parse(localStorage.getItem(columnStorageKey) || 'null');
        if (Array.isArray(saved)) {
          const kept = saved.filter((k) => all.includes(k));
          if (kept.length) next = kept;
        }
      } catch { /* localStorage ilegible: se usa el valor por defecto */ }
    }
    setVisibleColumns((prev) => (
      prev.length === next.length && prev.every((k, i) => k === next[i]) ? prev : next
    ));
    setColumnOrder((prev) => {
      const kept = prev.filter((k) => all.includes(k));
      const merged = [...kept, ...all.filter((k) => !kept.includes(k))];
      const same = merged.length === prev.length && merged.every((k, i) => k === prev[i]);
      return same ? prev : merged;
    });
  }, [columns, defaultVisible, columnStorageKey]);

  /* Avisa de la vista activa: lo usan los listados con dos conjuntos de datos. */
  useEffect(() => { onViewChange?.(activeView); }, [activeView, onViewChange]);

  useEffect(() => {
    if (!showColumnMenu) return undefined;
    const onDown = (event) => {
      const inside = (ref) => ref.current && ref.current.contains(event.target);
      if (!inside(columnMenuRef) && !inside(columnPopupRef)) setShowColumnMenu(false);
    };
    const onKey = (event) => { if (event.key === 'Escape') setShowColumnMenu(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [showColumnMenu]);

  /* ── Orden por columna ──────────────────────────────────────────────── */
  const [sort, setSort] = useState(null);

  const toggleSort = (key) => {
    setSort((prev) => {
      let next = null;
      if (!prev || prev.key !== key) next = { key, dir: 'asc' };
      else if (prev.dir === 'asc') next = { key, dir: 'desc' };
      onSortChange?.(next);
      return next;
    });
  };

  const shownColumns = useMemo(() => {
    const byKey = new Map(columns.map((c) => [c.key, c]));
    const ordered = columnOrder
      .filter((key) => visibleColumns.includes(key) && byKey.has(key))
      .map((key) => byKey.get(key));
    // Por si el listado acaba de añadir una columna que aún no está en el orden.
    const extra = columns.filter(
      (c) => visibleColumns.includes(c.key) && !columnOrder.includes(c.key),
    );
    return [...ordered, ...extra];
  }, [columns, visibleColumns, columnOrder]);

  const shownRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const value = (row) => (col.sortValue ? col.sortValue(row) : row[col.key]);
    const sorted = [...rows].sort((a, b) => compareValues(value(a), value(b)));
    return sort.dir === 'desc' ? sorted.reverse() : sorted;
  }, [rows, sort, columns]);

  /* ── Filtros plegables en pantallas bajas ────────────────────────────────
     En un portátil de 14" (o con la ventana estrecha) la barra de filtros es
     la que más alto roba: buscador + 4 desplegables en una fila, y en cuanto
     el ancho baja de 992 px pasan a apilarse en cuatro filas. Ahí los filtros
     van detrás de un botón con contador; el buscador sigue siempre visible y
     en pantalla grande no cambia nada (se ven todos, como hasta ahora). */
  const compactViewport = useCompactViewport();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilterCount = filters.filter(isFilterFilled).length;
  // Los filtros van SIEMPRE plegados detrás del botón «Filtros» (también en
  // pantalla grande): la barra de selectores ocupaba una fila entera en todos
  // los listados. El botón lleva el contador de filtros activos.
  const collapsibleFilters = filters.length > 0;
  const filtersVisible = !collapsibleFilters || filtersOpen;

  /* El buscador vive en la cabecera en todos los tamaños: así la barra de
     herramientas solo aparece cuando se abren los filtros. */
  const inlineSearch = Boolean(onSearchChange);
  const showToolbar = collapsibleFilters
    ? filtersOpen
    : filters.length > 0 || Boolean(onSearchChange);

  const filtersToggle = collapsibleFilters ? (
    <button
      type="button"
      className={`list-btn-secondary list-filters-btn${activeFilterCount ? ' is-active' : ''}`}
      onClick={() => setFiltersOpen((open) => !open)}
      aria-expanded={filtersOpen}
      title={tf('list.filters', 'Filtros')}
    >
      <FunnelIcon />
      {tf('list.filters', 'Filtros')}
      {activeFilterCount > 0 && (
        <span className="list-filters-count">{activeFilterCount}</span>
      )}
      {filtersOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
    </button>
  ) : null;

  /* ── Diálogo de borrado ─────────────────────────────────────────────── */
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Alternativa suave del diálogo: desactivar en vez de borrar (lo usa
  // Ubicaciones, que además necesita enseñar qué se queda sin ubicación).
  const [deactivating, setDeactivating] = useState(false);
  // Datos que se piden al abrir el diálogo para explicar el alcance del
  // borrado (`actions.prepareDelete`). Mientras llegan, se dice «Cargando…».
  const [deleteExtra, setDeleteExtra] = useState(null);
  const [deletePreparing, setDeletePreparing] = useState(false);
  // Grupos plegados (solo si el listado pide `groupBy`)
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());

  /**
   * Filas de la tabla, con las cabeceras de grupo intercaladas cuando el
   * listado pasa `groupBy`. Cada elemento es `{ kind: 'group'|'row', ... }`.
   *
   * El agrupado se hace con un `Map` para juntar TODAS las filas de la misma
   * etiqueta aunque no lleguen consecutivas: el backend ordena por CIDR o por
   * fecha, no por el campo del grupo. Antes se abría cabecera cada vez que
   * cambiaba la etiqueta, así que salían grupos repetidos («REUS (5)» cuatro
   * veces seguidas) y, como esas cabeceras compartían `key`, React descartaba
   * filas hermanas: el contador decía 5 y solo se veía una subred.
   */
  const tableBlocks = useMemo(() => {
    if (typeof groupBy !== 'function') return shownRows.map((row) => ({ kind: 'row', row }));
    const noValue = t('list.no_value') || 'Sin valor';
    const groups = new Map();
    shownRows.forEach((row) => {
      const label = groupBy(row) || noValue;
      const items = groups.get(label);
      if (items) items.push(row); else groups.set(label, [row]);
    });
    const blocks = [];
    [...groups.keys()]
      .sort((a, b) => compareValues(a, b))
      .forEach((label) => {
        const items = groups.get(label);
        blocks.push({ kind: 'group', label, count: items.length });
        items.forEach((row) => blocks.push({ kind: 'row', row, groupLabel: label }));
      });
    return blocks;
  }, [shownRows, groupBy, t]);

  const toggleGroup = (label) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  };

  /* ── Selección múltiple (edición masiva) ────────────────────────────── */
  const selectionEnabled = Boolean(selectable || bulkActions);
  const selectableRow = useCallback(
    (row) => (typeof selectable === 'function' ? Boolean(selectable(row)) : true),
    [selectable],
  );
  const keyOf = useCallback((row) => String(rowKey(row)), [rowKey]);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());

  /* La selección SOLO puede contener filas que se están viendo: si un filtro
     las esconde (o se recargan los datos), se caen. Así una acción masiva
     nunca toca algo que el usuario no tiene delante. */
  useEffect(() => {
    setSelectedKeys((prev) => {
      if (!prev.size) return prev;
      const visible = new Set(
        shownRows.filter((row) => selectableRow(row)).map((row) => keyOf(row)),
      );
      let changed = false;
      const next = new Set();
      prev.forEach((key) => {
        if (visible.has(key)) next.add(key); else changed = true;
      });
      return changed ? next : prev;
    });
  }, [shownRows, selectableRow, keyOf, selectionEnabled]);

  const selectedRows = useMemo(
    () => shownRows.filter((row) => selectableRow(row) && selectedKeys.has(keyOf(row))),
    [shownRows, selectableRow, keyOf, selectedKeys],
  );

  const toggleRowSelection = (row) => {
    const key = keyOf(row);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const selectableVisible = useMemo(
    () => shownRows.filter((row) => selectableRow(row)),
    [shownRows, selectableRow],
  );
  const allVisibleSelected = selectableVisible.length > 0
    && selectableVisible.every((row) => selectedKeys.has(keyOf(row)));
  const someVisibleSelected = !allVisibleSelected
    && selectableVisible.some((row) => selectedKeys.has(keyOf(row)));

  const toggleAllVisible = () => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        selectableVisible.forEach((row) => next.delete(keyOf(row)));
      } else {
        selectableVisible.forEach((row) => next.add(keyOf(row)));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedKeys(new Set());

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [selectionResetKey]);

  /**
   * Abre el diálogo de borrado. Si el listado pasa `actions.prepareDelete(row)`,
   * se pide el alcance (p. ej. los CIs que se quedarán sin ubicación) y se
   * pinta con `actions.deleteDetails(info, row)` antes de confirmar.
   */
  const openDeleteDialog = (row) => {
    setRowToDelete(row);
    setDeleteExtra(null);
    setDeletePreparing(false);
    if (typeof actions.prepareDelete !== 'function') return;
    setDeletePreparing(true);
    Promise.resolve(actions.prepareDelete(row))
      .then((info) => setDeleteExtra(info || null))
      .catch(() => setDeleteExtra(null))
      .finally(() => setDeletePreparing(false));
  };

  const closeDeleteDialog = () => {
    if (deleting || deactivating) return;
    setRowToDelete(null);
    setDeleteExtra(null);
    setDeletePreparing(false);
  };

  const confirmDelete = async () => {
    if (!rowToDelete) return;
    setDeleting(true);
    try {
      await actions.onDelete?.(rowToDelete);
    } finally {
      setDeleting(false);
      setRowToDelete(null);
      setDeleteExtra(null);
      setDeletePreparing(false);
    }
  };

  /** Alternativa al borrado: conservar el registro desactivándolo. */
  const confirmDeactivate = async () => {
    if (!rowToDelete) return;
    setDeactivating(true);
    try {
      await actions.onDeactivate?.(rowToDelete);
    } finally {
      setDeactivating(false);
      setRowToDelete(null);
      setDeleteExtra(null);
      setDeletePreparing(false);
    }
  };

  const can = (fn, row) => (typeof fn === 'function' ? fn(row) : fn !== false);
  const columnLabel = (col) => (typeof col.label === 'string' ? col.label : col.key);
  const cellValue = (col, row) => (col.render ? col.render(row) : row[col.key]);

  const hasRowActions = Boolean(
    actions.extra || actions.onEdit || actions.onCopy || actions.onDelete,
  );

  /**
   * Botones de una fila. Es UNA función para que la tabla y las vistas propias
   * (fichas, kanban…) pinten exactamente los mismos botones, en el mismo orden:
   * `[acciones propias] · Copiar · Editar · Eliminar`.
   * El contenedor corta la propagación: pulsar un botón no abre la ficha.
   */
  const renderRowActions = (row) => (
    <div className="list-actions" onClick={(event) => event.stopPropagation()}>
      {actions.extra?.(row)}

      {can(actions.canCopy, row) && actions.onCopy && (
        <button
          type="button"
          className="list-action-btn"
          onClick={() => actions.onCopy(row)}
          title={actions.copyTitle || tf('action.copy', 'Copiar')}
        >
          <DocumentDuplicateIcon />
        </button>
      )}

      {can(actions.canEdit, row) && actions.onEdit && (
        <button
          type="button"
          className="list-action-btn"
          onClick={() => actions.onEdit(row)}
          title={actions.editTitle || tf('action.edit', 'Editar')}
        >
          <PencilSquareIcon />
        </button>
      )}

      {can(actions.canDelete, row) && actions.onDelete && (
        <button
          type="button"
          className="list-action-btn list-action-btn--danger"
          onClick={() => openDeleteDialog(row)}
          title={actions.deleteButtonTitle || tf('action.delete', 'Eliminar')}
        >
          <TrashIcon />
        </button>
      )}
    </div>
  );

  const showEmpty = !loading && !error && shownRows.length === 0;

  return (
    <div className={`list-page${embedded ? ' list-page--embedded' : ''}${compactViewport ? ' list-page--compact' : ''} ${className}`.trim()}>
      {/* ══ Cabecera ══ */}
      <div className="list-head">
        <div className="list-head-left">
          {Icon && <Icon className="list-head-icon" />}
          <div>
            {embedded
              ? <h2 className="list-section-title">{title}</h2>
              : <h1 className="list-title">{title}</h1>}
            {subtitle && (
              embedded
                ? <p className="list-section-subtitle">{subtitle}</p>
                : <p className="list-subtitle">{subtitle}</p>
            )}
          </div>
        </div>

        {/* El buscador y el botón de filtros viven en la cabecera: la barra de
            selectores queda plegada y solo se pinta al abrirla. */}
        {(inlineSearch || collapsibleFilters) && (
          <div className="list-head-search">
            {inlineSearch && (
              <input
                type="search"
                className="list-search list-search--inline"
                placeholder={searchPlaceholder}
                value={search ?? ''}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            )}
            {collapsibleFilters && filtersToggle}
          </div>
        )}

        <div className="list-head-actions">
          {headerActions}

          {importEntity && (
            <button
              type="button"
              className="list-btn-secondary"
              onClick={() => setImportOpen(true)}
              title={importTitle || tf('list.import', 'Importar desde Excel o CSV')}
            >
              <ArrowUpTrayIcon style={{ width: 15, height: 15 }} />
              {!compactViewport && (tf('list.import', 'Importar'))}
            </button>
          )}

          {/* Exportar a Excel: arriba, junto a las vistas (antes estaba abajo, en la barra de filtros) */}
          {exportRows && (
            <ExportExcelButton
              rows={exportRows}
              fileBase={exportFileBase}
              sheetName={exportSheetName || String(title || 'Listado')}
              className="list-btn-secondary"
              // En compacto solo el icono: el título del botón lo explica.
              iconOnly={compactViewport}
            />
          )}

          <ViewSwitcher
            views={viewDefs}
            value={activeView}
            onChange={setView}
            onReset={resetView}
            isDefault={isDefaultView}
            defaultLabel={viewDefs.find((v) => v.key === defaultView)?.label}
          />
          {onCreate && (
            <button
              type="button"
              className="list-btn-primary"
              onClick={onCreate}
              disabled={createDisabled}
              title={createTitle}
            >
              <span aria-hidden="true">＋</span>
              {createLabel || tf('action.create', 'Crear')}
            </button>
          )}
        </div>
      </div>

      {/* ══ KPIs opcionales ══ */}
      {kpis}

      {/* ══ Barra de herramientas ══
          En pantalla baja desaparece (buscador y filtros viven en la cabecera);
          solo vuelve, por debajo, cuando el usuario abre los filtros. */}
      {showToolbar && (
      <div className={`list-toolbar${collapsibleFilters ? ' list-toolbar--collapsible' : ''}`}>
        <div className="list-toolbar-left">
          {onSearchChange && !inlineSearch && (
            <input
              type="search"
              className="list-search"
              placeholder={searchPlaceholder}
              value={search ?? ''}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          )}
          {!inlineSearch && filtersToggle}
          <div className={`list-toolbar-filters${filtersVisible ? '' : ' is-collapsed'}`}>
            {filters.map((filter) => (
              filter.type === 'locations' ? (
                <LocationFilter
                  key={filter.key}
                  companyId={filter.companyId}
                  value={filter.value || []}
                  onChange={filter.onChange}
                  label={filter.label}
                  showPath
                  onlyIds={filter.onlyIds || null}
                  counts={filter.counts || null}
                  panelAlign="left"
                />
              ) : filter.type === 'toggle' ? (
                <button
                  key={filter.key}
                  type="button"
                  className="list-btn-secondary"
                  aria-pressed={Boolean(filter.value)}
                  onClick={() => filter.onChange(filter.value ? '' : (filter.onValue ?? filter.key))}
                  title={filter.title || filter.label}
                  style={filter.value
                    ? { borderColor: 'rgba(var(--accent-rgb), 0.5)', color: 'var(--accent-l)' }
                    : undefined}
                >
                  {filter.value ? '✓ ' : ''}{filter.label}
                </button>
              ) : (
                <select
                  key={filter.key}
                  className="list-select"
                  value={filter.value ?? ''}
                  onChange={(e) => filter.onChange(e.target.value)}
                  title={filter.label}
                >
                  <option value="">{filter.allLabel || filter.label}</option>
                  {(filter.options || []).map((opt) => (
                    <option key={String(opt.value)} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              )
            ))}
          </div>
        </div>

      </div>
      )}

      {/* ══ Carga / error ══ */}
      {loading && (
        <div className="list-loading">
          <div className="spinner-border text-primary" aria-hidden="true" />
          <span>{loadingText}</span>
        </div>
      )}
      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      {/* ══ Barra de selección masiva ══ */}
      {selectionEnabled && selectedRows.length > 0 && (
        <div className="list-bulk-bar">
          <span className="list-bulk-count">
            <strong>{selectedRows.length}</strong>{' '}
            {selectedRows.length === 1
              ? tf('list.selected_one', 'seleccionado')
              : tf('list.selected', 'seleccionados')}
          </span>
          <div className="list-bulk-actions">
            {bulkActions?.({ selectedRows, clearSelection })}
          </div>
          <button type="button" className="list-btn-secondary" onClick={clearSelection}>
            {tf('list.clear_selection', 'Quitar selección')}
          </button>
        </div>
      )}

      {/* ══ Tabla ══ */}
      {!loading && !error && !showEmpty && showsTable && (
        <div className="list-table-wrap">
          <table className="list-table">
            <thead>
              <tr>
                {selectionEnabled && (
                  <th className="list-th-select">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
                      onChange={toggleAllVisible}
                      title={tf('list.select_all', 'Seleccionar todo lo visible')}
                      aria-label={tf('list.select_all', 'Seleccionar todo lo visible')}
                    />
                  </th>
                )}
                {shownColumns.map((col) => (
                  <th
                    key={col.key}
                    className={[
                      col.sortable ? 'list-th-sortable' : '',
                      'list-th-draggable',
                      dragHeader === col.key ? 'is-dragging' : '',
                      headerDropTarget === col.key && dragHeader && dragHeader !== col.key ? 'is-drop-target' : '',
                      col.headerClassName || '',
                    ].filter(Boolean).join(' ')}
                    style={col.width ? { width: col.width } : undefined}
                    onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                    // Arrastrar la cabecera recoloca la columna EN EL SITIO. Un
                    // clic sin arrastre sigue ordenando por esa columna.
                    draggable
                    onDragStart={(event) => {
                      setDragHeader(col.key);
                      event.dataTransfer.effectAllowed = 'move';
                      try { event.dataTransfer.setData('text/plain', col.key); } catch { /* noop */ }
                    }}
                    onDragOver={(event) => {
                      event.preventDefault();
                      event.dataTransfer.dropEffect = 'move';
                      if (headerDropTarget !== col.key) setHeaderDropTarget(col.key);
                    }}
                    onDragLeave={() => setHeaderDropTarget((prev) => (prev === col.key ? null : prev))}
                    onDrop={(event) => {
                      event.preventDefault();
                      const from = dragHeader || event.dataTransfer.getData('text/plain');
                      setDragHeader(null);
                      setHeaderDropTarget(null);
                      if (from && from !== col.key) moveColumn(from, col.key);
                    }}
                    onDragEnd={() => { setDragHeader(null); setHeaderDropTarget(null); }}
                    title={
                      col.sortable
                        ? `${tf('list.sort_by', 'Ordenar por')} ${columnLabel(col)} · arrastra para mover la columna`
                        : 'Arrastra para mover la columna'
                    }
                  >
                    {columnLabel(col)}
                    {sort?.key === col.key && (
                      <span className="list-sort-indicator">{sort.dir === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </th>
                ))}
                {hasRowActions && (
                  <th className="list-actions-header">{tf('action.actions', 'Acciones')}</th>
                )}
                {/* Rueda dentada: mostrar/ocultar columnas, a la derecha de la cabecera */}
                <th className="list-th-cog">
                  <button
                    ref={columnMenuRef}
                    type="button"
                    className="list-cog-btn"
                    onClick={toggleColumnMenu}
                    title={tf('list.toggle_columns', 'Mostrar / ocultar columnas')}
                    aria-expanded={showColumnMenu}
                  >
                    <Cog6ToothIcon />
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {tableBlocks.map((block) => {
                if (block.kind === 'group') {
                  const isCollapsed = collapsedGroups.has(block.label);
                  return (
                    <tr key={`group:${block.label}`} className="list-group-row">
                      <td colSpan={shownColumns.length + (hasRowActions ? 1 : 0) + (selectionEnabled ? 1 : 0) + 1}>
                        <button
                          type="button"
                          className="list-group-toggle"
                          onClick={() => toggleGroup(block.label)}
                        >
                          {isCollapsed ? '▸' : '▾'} <span>{block.label}</span> <strong>({block.count})</strong>
                        </button>
                      </td>
                    </tr>
                  );
                }
                const row = block.row;
                if (block.groupLabel && collapsedGroups.has(block.groupLabel)) return null;
                return (
                <tr
                  key={rowKey(row)}
                  id={rowId ? rowId(row) : undefined}
                  className={[
                    onRowClick ? 'list-row-clickable' : '',
                    rowClassName ? (rowClassName(row) || '') : '',
                  ].filter(Boolean).join(' ')}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onKeyDown={onRowClick ? (e) => {
                    if (e.key === 'Enter' && e.target === e.currentTarget) onRowClick(row);
                  } : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  title={rowTitle ? rowTitle(row) : undefined}
                >
                  {selectionEnabled && (
                    <td className="list-select-cell" onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedKeys.has(keyOf(row))}
                        disabled={!selectableRow(row)}
                        onChange={() => toggleRowSelection(row)}
                        aria-label={tf('list.select_row', 'Seleccionar')}
                      />
                    </td>
                  )}
                  {shownColumns.map((col) => {
                    const value = cellValue(col, row);
                    // En móvil las celdas vacías se ocultan: si no, cada fila
                    // enseña una retahíla de «—» sin información. Muchos
                    // listados pintan el hueco con `<span className="list-hint">—</span>`,
                    // así que también cuenta como vacío un elemento cuyo único
                    // contenido es un guion.
                    const dashedNode = React.isValidElement(value)
                      && (value.props?.children === '—'
                        || value.props?.children === '–'
                        || value.props?.children === '-');
                    const isEmpty = value === null
                      || value === undefined
                      || (typeof value === 'string' && value.trim() === '')
                      || (typeof value === 'string' && /^[-–—\s]+$/.test(value))
                      || dashedNode;
                    return (
                      <td
                        key={col.key}
                        className={[col.className, isEmpty ? 'list-cell--empty' : '']
                          .filter(Boolean).join(' ')}
                        style={col.style}
                        data-label={columnLabel(col)}
                      >
                        {isEmpty ? null : value}
                      </td>
                    );
                  })}

                  {hasRowActions && (
                    <td onClick={(e) => e.stopPropagation()}>
                      {renderRowActions(row)}
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ══ Vista propia del listado (fichas, kanban…) ══ */}
      {!loading && !error && (!showEmpty || viewRendersEmpty) && !showsTable && activeViewDef?.render && (
        <div className="list-view-body">
          {activeViewDef.render({
            rows: shownRows,
            columns: shownColumns,
            rowActions: renderRowActions,
            hasRowActions,
            onRowClick,
            rowKey,
          })}
        </div>
      )}

      {/* ══ Sin datos ══ */}
      {showEmpty && !viewRendersEmpty && (
        <div className="list-empty">
          <span aria-hidden="true">{empty.icon || '📄'}</span>
          <p className="empty-title">{empty.title || tf('msg.no_data', 'No hay datos')}</p>
          {empty.subtitle && <p className="empty-subtitle">{empty.subtitle}</p>}
          {empty.action}
        </div>
      )}

      {/* ══ Menú de columnas (fuera de la tabla: `position: fixed`) ══ */}
      {showColumnMenu && (
        <div
          ref={columnPopupRef}
          className="list-column-dropdown"
          style={{
            position: 'fixed',
            top: columnMenuPos?.top ?? 'auto',
            right: columnMenuPos?.right ?? 'auto',
            zIndex: 1080,
          }}
        >
          {columnOrder.map((key) => {
            const col = columns.find((c) => c.key === key);
            if (!col) return null;
            return (
              <label
                key={col.key}
                className="list-column-item"
                draggable
                onDragStart={(event) => {
                  setDragColumn(col.key);
                  event.dataTransfer.effectAllowed = 'move';
                  // Firefox exige datos en el dragstart para iniciar el arrastre.
                  try { event.dataTransfer.setData('text/plain', col.key); } catch { /* noop */ }
                }}
                onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'move'; }}
                onDrop={(event) => {
                  event.preventDefault();
                  moveColumn(dragColumn || event.dataTransfer.getData('text/plain'), col.key);
                  setDragColumn(null);
                }}
                onDragEnd={() => setDragColumn(null)}
                style={{
                  opacity: dragColumn === col.key ? 0.45 : 1,
                  cursor: 'grab',
                  borderTop: dragColumn && dragColumn !== col.key ? '1px dashed var(--border)' : undefined,
                }}
                title="Arrastra para cambiar el orden"
              >
                <span aria-hidden="true" style={{ color: 'var(--text-muted)', letterSpacing: '-2px' }}>⠿</span>
                <input
                  type="checkbox"
                  checked={visibleColumns.includes(col.key)}
                  onChange={() => toggleColumn(col.key)}
                />
                <span>{columnLabel(col)}</span>
              </label>
            );
          })}
          <button
            type="button"
            className="list-btn-secondary"
            style={{ width: '100%', marginTop: 6, justifyContent: 'center' }}
            onClick={resetColumns}
          >
            Restablecer columnas
          </button>
        </div>
      )}

      {/* ══ Importador genérico ══ */}
      {importOpen && importEntity && (
        <ImportDialog
          entity={importEntity}
          companyId={companyId}
          onClose={() => setImportOpen(false)}
          onDone={(resumen) => {
            setImportOpen(false);
            onImported?.(resumen);
          }}
        />
      )}

      {/* ══ Diálogo de confirmación de borrado ══ */}
      {rowToDelete && (
        <Dialog
          title={actions.deleteTitle || tf('action.delete', 'Eliminar')}
          onClose={closeDeleteDialog}
          busy={deleting || deactivating}
          footer={(
            <>
              <button
                type="button"
                className="list-btn-secondary"
                onClick={closeDeleteDialog}
                disabled={deleting || deactivating}
              >
                {tf('action.cancel', 'Cancelar')}
              </button>
              {actions.onDeactivate && can(actions.canDeactivate, rowToDelete) && (
                <button
                  type="button"
                  className="list-btn-secondary"
                  onClick={confirmDeactivate}
                  disabled={deleting || deactivating}
                  title={actions.deactivateTitle || tf('action.deactivate', 'Desactivar')}
                >
                  {deactivating
                    ? tf('action.deactivating', 'Desactivando…')
                    : (actions.deactivateLabel || tf('action.deactivate', 'Desactivar'))}
                </button>
              )}
              <button
                type="button"
                className="list-btn-danger"
                onClick={confirmDelete}
                disabled={deleting || deactivating}
              >
                {deleting ? tf('action.deleting', 'Eliminando…') : tf('action.delete', 'Eliminar')}
              </button>
            </>
          )}
        >
          <p>
            {actions.deleteMessage
              ? actions.deleteMessage(rowToDelete)
              : `${tf('msg.confirm_delete', '¿Seguro que quieres eliminar este registro?')}`}
          </p>
          {deletePreparing && (
            <p className="list-hint">{tf('msg.loading', 'Cargando…')}</p>
          )}
          {!deletePreparing && deleteExtra && typeof actions.deleteDetails === 'function' && (
            actions.deleteDetails(deleteExtra, rowToDelete)
          )}
        </Dialog>
      )}
    </div>
  );
}
